"use client";

import React from "react";
import { ShieldCheck, Loader, Wine, Utensils, Flower2, Anchor, CalendarDays } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useWatch } from "react-hook-form";
import { projects } from "./Projects";
const minimalInputClass = "border-x-0 border-t-0 border-b border-border rounded-md bg-transparent px-0 py-3 h-auto text-base focus-visible:ring-0 focus-visible:border-primary focus-visible:shadow-none transition-all shadow-none placeholder:text-muted-foreground/30";
const PRICES = {
  CHAMPAGNE: 450,
  CRUDITES: 350,
  YOGA: 500
};
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const champagneQty = useWatch({
    control,
    name: "champagne_premium_qty"
  }) || 0;
  const cruditesQty = useWatch({
    control,
    name: "crudites_luxury_qty"
  }) || 0;
  const yogaToggle = useWatch({
    control,
    name: "yoga_private_toggle"
  }) || false;
  const boatSelection = useWatch({
    control,
    name: "boat_selection"
  }) || "";
  const charterDays = useWatch({
    control,
    name: "charter_duration_days"
  }) || 1;
  const selectedBoat = projects.find(p => p.title === boatSelection);
  const boatPrice = selectedBoat ? Number(selectedBoat.price) : 0;
  const boatTotal = boatPrice * Number(charterDays);
  const champagneTotal = Number(champagneQty) * PRICES.CHAMPAGNE;
  const cruditesTotal = Number(cruditesQty) * PRICES.CRUDITES;
  const yogaTotal = yogaToggle ? PRICES.YOGA : 0;
  const grandTotal = champagneTotal + cruditesTotal + yogaTotal + boatTotal;
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in duration-700">
                <div className="size-16 rounded-md bg-primary/10 flex items-center justify-center mb-4">
                    <ShieldCheck className="size-8 text-primary" />
                </div>
                <h3 className="text-2xl font-default text-foreground">Richiesta Inviata con Successo</h3>
                <p className="text-muted-foreground max-w-md">
                    {successMessage}
                </p>
                <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/30 hover:bg-primary/10 text-primary rounded-md">
                    Invia un'altra richiesta
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
            <div className="space-y-6">
                <FormField control={control} name="full_name" rules={{
        required: "Il nome è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-medium">Nome Completo</FormLabel>
                            <FormControl>
                                <Input placeholder="Es. Mario Rossi" className={minimalInputClass} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs opacity-50">Inserisca il nome del referente per la consulenza.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField control={control} name="email" rules={{
          required: "L'email è obbligatoria",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Inserisca un indirizzo email valido"
          }
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-medium">Email Professionale</FormLabel>
                                <FormControl>
                                    <Input type="email" placeholder="mario.rossi@azienda.it" className={minimalInputClass} {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="phone" rules={{
          required: "Il numero è obbligatorio",
          pattern: {
            value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
            message: "Inserisca un numero di telefono valido"
          }
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-medium">Numero di Telefono</FormLabel>
                                <FormControl>
                                    <Input type="tel" placeholder="+39 333 1234567" className={minimalInputClass} {...field} />
                                </FormControl>
                                <FormDescription className="text-xs opacity-50">La contatteremo con la massima discrezione.</FormDescription>
                                <FormMessage />
                            </FormItem>} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField control={control} name="event_date" rules={{
          required: "Il periodo è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-medium">Periodo Desiderato</FormLabel>
                                <FormControl>
                                    <Input type="date" className={cn(minimalInputClass, "dark:[color-scheme:dark]")} {...field} />
                                </FormControl>
                                <FormDescription className="text-xs opacity-50">Selezioni la data di inizio charter.</FormDescription>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="event_time" rules={{
          required: "L'orario è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-medium">Orario Preferito</FormLabel>
                                <FormControl>
                                    <Input type="time" placeholder="10:00" className={cn(minimalInputClass, "dark:[color-scheme:dark]")} {...field} />
                                </FormControl>
                                <FormDescription className="text-xs opacity-50">Formato 24 ore.</FormDescription>
                                <FormMessage />
                            </FormItem>} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4 border-t border-secondary/10">
                    <FormField control={control} name="champagne_premium_qty" render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-[10px] font-bold flex items-center gap-2">
                                    <Wine className="size-3 text-secondary" /> Champagne Premium
                                </FormLabel>
                                <FormControl>
                                    <Input type="number" min="0" placeholder="0" className={minimalInputClass} {...field} onChange={e => field.onChange(parseInt(e.target.value) || 0)} />
                                </FormControl>
                                <FormDescription className="text-[10px] opacity-50">€450 / bottiglia</FormDescription>
                            </FormItem>} />
                    <FormField control={control} name="crudites_luxury_qty" render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-[10px] font-bold flex items-center gap-2">
                                    <Utensils className="size-3 text-secondary" /> Crudités Luxury
                                </FormLabel>
                                <FormControl>
                                    <Input type="number" min="0" placeholder="0" className={minimalInputClass} {...field} onChange={e => field.onChange(parseInt(e.target.value) || 0)} />
                                </FormControl>
                                <FormDescription className="text-[10px] opacity-50">€350 / plateau</FormDescription>
                            </FormItem>} />
                    <FormField control={control} name="yoga_private_toggle" render={({
          field
        }) => <FormItem className="flex flex-col justify-between">
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-[10px] font-bold flex items-center gap-2">
                                    <Flower2 className="size-3 text-secondary" /> Yoga Privato
                                </FormLabel>
                                <FormControl>
                                    <div className="flex items-center h-full pt-2">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input type="checkbox" className="sr-only peer" checked={field.value} onChange={e => field.onChange(e.target.checked)} />
                                            <div className="w-11 h-6 bg-muted rounded-lg peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-lg after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary"></div>
                                            <span className="ms-3 text-xs font-medium text-muted-foreground">{field.value ? 'Incluso' : 'Escluso'}</span>
                                        </label>
                                    </div>
                                </FormControl>
                                <FormDescription className="text-[10px] opacity-50">€500 / sessione</FormDescription>
                            </FormItem>} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField control={control} name="boat_selection" rules={{
          required: "La selezione della barca è obbligatoria"
        }} render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-medium flex items-center gap-2">
                                    <Anchor className="size-3 text-secondary" /> Selezione Imbarcazione
                                </FormLabel>
                                <FormControl>
                                    <select className={cn(minimalInputClass, "w-full appearance-none cursor-pointer", !field.value && "text-muted-foreground/50")} {...field}>
                                        <option value="" disabled>Selezioni la barca</option>
                                        {projects.map((project, i) => <option key={project.title} value={project.title} data-index={i} className="bg-background text-foreground py-2">
                                                {project.title} - Da €{Number(project.price).toLocaleString()}/giorno
                                            </option>)}
                                    </select>
                                </FormControl>
                                <FormMessage />
                            </FormItem>} />

                    <FormField control={control} name="charter_duration_days" render={({
          field
        }) => <FormItem>
                                <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-medium flex items-center gap-2">
                                    <CalendarDays className="size-3 text-secondary" /> Durata Charter (Giorni)
                                </FormLabel>
                                <FormControl>
                                    <Input type="number" min="1" className={minimalInputClass} {...field} onChange={e => field.onChange(parseInt(e.target.value) || 1)} />
                                </FormControl>
                                <FormDescription className="text-xs opacity-50">Inserisca il numero di giorni desiderati.</FormDescription>
                                <FormMessage />
                            </FormItem>} />
                </div>

                <FormField control={control} name="special_requests" render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-muted-foreground uppercase tracking-widest text-xs font-medium">Richieste Speciali</FormLabel>
                            <FormControl>
                                <textarea placeholder="Dettagli sull'itinerario, servizi di bordo o esigenze particolari..." className={cn(minimalInputClass, "w-full min-h-[100px] resize-y")} {...field} />
                            </FormControl>
                            <FormDescription className="text-xs opacity-50">Fornisca ogni dettaglio utile per personalizzare la sua esperienza.</FormDescription>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {grandTotal > 0 && <div className="bg-secondary/5 border border-secondary/20 p-6 rounded-lg space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-secondary border-b border-secondary/10 pb-2">Riepilogo Configurazione</h4>
                    <div className="space-y-2">
                        {selectedBoat && <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Barca: {selectedBoat.title} ({charterDays} {charterDays === 1 ? 'giorno' : 'giorni'})</span>
                                <span className="font-medium">€{boatTotal.toLocaleString()}</span>
                            </div>}
                        {champagneQty > 0 && <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Champagne Premium (x{champagneQty})</span>
                                <span className="font-medium">€{champagneTotal.toLocaleString()}</span>
                            </div>}
                        {cruditesQty > 0 && <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Crudités Luxury (x{cruditesQty})</span>
                                <span className="font-medium">€{cruditesTotal.toLocaleString()}</span>
                            </div>}
                        {yogaToggle && <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">Lezione Yoga Privata</span>
                                <span className="font-medium">€{yogaTotal.toLocaleString()}</span>
                            </div>}
                    </div>
                    <div className="flex justify-between items-center pt-4 border-t border-secondary/20">
                        <span className="text-sm font-bold uppercase tracking-tighter">Totale Stimato</span>
                        <span className="text-xl font-bold text-primary">€{grandTotal.toLocaleString()}</span>
                    </div>
                </div>}

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                    <p className="text-destructive text-sm text-center">{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-default tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 shadow-md transition-all duration-500 border-none rounded-md">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Elaborazione in corso...
                    </> : 'Richiedi Consulenza Riservata'}
            </Button>
        </Form>;
}
export default function SecureYourEvening() {
  return <section data-section-id="240360" id="consulenza-riservata" className="bg-[#FAF9F6] text-foreground py-24 px-4 relative overflow-hidden">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-secondary/5 rounded-lg blur-[120px] pointer-events-none" aria-hidden="true" />

            <div className="max-w-[600px] mx-auto relative z-10">
                <div className="flex flex-col items-center text-center mb-12">
                    <Badge variant="outline" className="border-secondary/30 text-secondary bg-secondary/5 px-4 py-1.5 mb-8 rounded-lg flex items-center gap-2 shadow-sm">
                        <ShieldCheck className="size-4" />
                        <span className="tracking-widest uppercase text-xs font-semibold">Protocollo di Riservatezza SSL</span>
                    </Badge>
                    
                    <h2 className="text-3xl md:text-4xl font-default font-bold mb-4 uppercase tracking-tight">Consulenza Riservata</h2>
                    <p className="text-muted-foreground font-default">
                        Inserisca i suoi dati per un preventivo personalizzato. Ogni richiesta è gestita con il massimo rigore professionale e assoluta privacy.
                    </p>
                </div>

                <div className="bg-white backdrop-blur-sm border border-secondary/20 p-8 md:p-10 rounded-lg shadow-xl">
                    <FormProvider formKey="consultation_form_collection_gold" formId="consultation_form_collection_gold" sectionName="Consulenza Riservata" defaultValues={{
          full_name: "",
          email: "",
          phone: "",
          event_date: "",
          event_time: "",
          boat_selection: "",
          charter_duration_days: 1,
          special_requests: "",
          champagne_premium_qty: 0,
          crudites_luxury_qty: 0,
          yoga_private_toggle: false
        }} validationRules={{
          full_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: true,
            pattern: "phone"
          },
          event_date: {
            required: true
          },
          event_time: {
            required: true
          },
          boat_selection: {
            required: true
          }
        }} submitText="Richiedi Consulenza Riservata" successMessage="La sua richiesta è stata trasmessa in modo sicuro. Il nostro team di broker la contatterà entro 24 ore per definire i dettagli.">
                        <ReservationForm />
                    </FormProvider>
                </div>
            </div>
        </section>;
}